import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';

type PnlLine = {
  organization_id: string; period: string; kind: string; cost_type: string | null; amount: number;
  property_id: string | null; contract_id: string | null; client_id: string | null; service_line_id: string | null;
  property_name: string | null; client_name: string | null; contract_name: string | null; service_line_name: string | null;
};
type Variance = { fiscal_year: number; cost_type: string; currency: string; property_name: string | null; client_name: string | null; budget: number; actual: number };
type Dim = 'total' | 'property' | 'contract' | 'client' | 'service_line';

export function Pnl() {
  const { t, i18n } = useTranslation();
  const { currency } = useOrg();
  const loc = i18n.language;
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01`);
  const [to, setTo] = useState(`${year}-12`);
  const [dim, setDim] = useState<Dim>('property');
  const [lines, setLines] = useState<PnlLine[]>([]);
  const [variance, setVariance] = useState<Variance[]>([]);
  const money = (n: number) => formatCurrency(n, currency, loc);

  const load = useCallback(async () => {
    const start = `${from}-01`;
    const end = `${to}-28`;
    const [pl, bv] = await Promise.all([
      supabase.from('v_pnl_lines').select('*').gte('period', start).lte('period', end),
      supabase.from('v_budget_variance').select('fiscal_year,cost_type,currency,property_name,client_name,budget,actual'),
    ]);
    setLines((pl.data as any) ?? []);
    setVariance((bv.data as any) ?? []);
  }, [from, to]);
  useEffect(() => { void load(); }, [load]);

  const keyOf = (l: PnlLine) => {
    switch (dim) {
      case 'property': return l.property_name ?? '—';
      case 'contract': return l.contract_name ?? '—';
      case 'client': return l.client_name ?? '—';
      case 'service_line': return l.service_line_name ?? '—';
      default: return t('pnl.allOrg');
    }
  };
  const groups = useMemo(() => {
    const m = new Map<string, { revenue: number; expense: number }>();
    for (const l of lines) {
      const k = keyOf(l);
      const g = m.get(k) ?? { revenue: 0, expense: 0 };
      if (l.kind === 'revenue') g.revenue += Number(l.amount); else g.expense += Number(l.amount);
      m.set(k, g);
    }
    return [...m.entries()].map(([k, v]) => ({ k, ...v, margin: v.revenue - v.expense })).sort((a, b) => b.margin - a.margin);
  }, [lines, dim]);
  const totals = useMemo(() => groups.reduce((s, g) => ({ revenue: s.revenue + g.revenue, expense: s.expense + g.expense, margin: s.margin + g.margin }), { revenue: 0, expense: 0, margin: 0 }), [groups]);

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 py-6">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{t('nav.pnl')}</h1>
            <p className="text-sm text-muted">{t('pnl.subtitle')}</p>
          </div>
          <div className="flex items-end gap-2 text-sm">
            <label className="flex flex-col gap-1"><span className="text-xs text-muted">{t('pnl.from')}</span><input type="month" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1.5" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs text-muted">{t('pnl.to')}</span><input type="month" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-md border border-border bg-card px-2 py-1.5" /></label>
            <label className="flex flex-col gap-1"><span className="text-xs text-muted">{t('pnl.by')}</span>
              <select value={dim} onChange={(e) => setDim(e.target.value as Dim)} className="rounded-md border border-border bg-card px-2 py-1.5">
                {(['total', 'property', 'contract', 'client', 'service_line'] as const).map((d) => <option key={d} value={d}>{t('pnl.dim_' + d)}</option>)}
              </select>
            </label>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-3 gap-3">
          {[['revenue', totals.revenue], ['expense', totals.expense], ['margin', totals.margin]].map(([k, v]) => (
            <Card key={k as string} className="p-4">
              <div className="text-xs uppercase tracking-wide text-muted">{t('pnl.' + k)}</div>
              <div className={cn('mt-1 text-2xl font-semibold', k === 'margin' && (v as number) < 0 ? 'text-st-overdue-fg' : '')}>{money(v as number)}</div>
            </Card>
          ))}
        </div>

        <Card className="mb-6 overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('pnl.byDimension', { dim: t('pnl.dim_' + dim) })}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted"><th className="px-4 py-2">{t('pnl.dim_' + dim)}</th><th className="px-4 py-2 text-right">{t('pnl.revenue')}</th><th className="px-4 py-2 text-right">{t('pnl.expense')}</th><th className="px-4 py-2 text-right">{t('pnl.margin')}</th></tr></thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.k} className="border-t border-divider">
                    <td className="px-4 py-2">{g.k}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(g.revenue)}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(g.expense)}</td>
                    <td className={cn('px-4 py-2 text-right font-mono', g.margin < 0 ? 'text-st-overdue-fg' : '')}>{money(g.margin)}</td>
                  </tr>
                ))}
                {groups.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-muted">{t('common.noData')}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('pnl.budgetVsActual')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted"><th className="px-4 py-2">{t('pnl.year')}</th><th className="px-4 py-2">{t('pnl.costType')}</th><th className="px-4 py-2">{t('pnl.scope')}</th><th className="px-4 py-2 text-right">{t('pnl.budget')}</th><th className="px-4 py-2 text-right">{t('pnl.actual')}</th><th className="px-4 py-2 text-right">{t('pnl.variance')}</th></tr></thead>
              <tbody>
                {variance.map((v, i) => {
                  const diff = Number(v.budget) - Number(v.actual);
                  return (
                    <tr key={i} className="border-t border-divider">
                      <td className="px-4 py-2">{v.fiscal_year}</td>
                      <td className="px-4 py-2">{t('cost.' + v.cost_type)}</td>
                      <td className="px-4 py-2 text-xs text-muted">{v.property_name || v.client_name || t('pnl.allOrg')}</td>
                      <td className="px-4 py-2 text-right font-mono">{money(Number(v.budget))}</td>
                      <td className="px-4 py-2 text-right font-mono">{money(Number(v.actual))}</td>
                      <td className={cn('px-4 py-2 text-right font-mono', diff < 0 ? 'text-st-overdue-fg' : 'text-st-done-fg')}>{money(diff)}</td>
                    </tr>
                  );
                })}
                {variance.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted">{t('pnl.noBudgets')}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
